import * as React from 'react';
import {
  CardHcd,
  CenterHcd,
  PublicInformation,
} from '@maiia/model/generated/model/api-patient/api-patient';
import { asyncActions } from '@docavenue/core';
import { get, isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import { useCurrentUser } from '@docavenue/components';

import {
  IconHeart,
  IconHeartFilled,
  IconStar,
  IconStarFilled,
} from '@maiia/design-system';

import { Stack, Typography } from '@mui/material';
import { analyticsEvent } from '@/src/analytic_tag_manager';

import Avatar from '../../atoms/Avatar/Avatar';

import { getSpecialityName } from '../../utils/card';

import { useTranslation } from '@/src/i18n';

import IconSwitcher from '../../atoms/IconSwitcher/IconSwitcher';
import { isOnlineTlc, pictures } from '@/src/utils';
import FavoriteSelector from './FavoriteSelector';
import DialogSlider from '../DialogSlider/DialogSlider';
import { getAvatar, getCardFullName, getSubstitute } from '../../utils';
import {
  authActions,
  favoritePractitionersActions,
  usersActions,
} from '@/src/actions';
import { enqueueSnackbar } from '@/src/actions/snackbar';
import RDVButton from './RDVButton';
import { APP_ROUTES } from '@/src/constants';
import Dialog from '@/components/molecules/Dialog/Dialog';

type Props = {
  card: CardHcd &
    Required<Pick<CardHcd, 'consultationReasons' | 'center'>> & {
      center: Required<Pick<CenterHcd, 'practitioners' | 'speciality'>>;
      publicInformation: PublicInformation;
    };
};

const PratHeader = ({ card }: Props) => {
  const { t } = useTranslation();
  const router = useRouter();
  const account = useCurrentUser();
  const [openSlider, setOpenSlider] = React.useState(false);
  const { isSubstitute, substitute } = getSubstitute(card);
  const fullName =
    router.query.fromImmediateTlc && isSubstitute
      ? `${substitute?.firstName} ${substitute?.lastName}`
      : getCardFullName(t, card);
  const [showFavoriteSelector, setShowFavoriteSelector] = React.useState(false);
  const cards = useSelector(state => get(state, 'cards.items'));
  const dispatch = useDispatch();

  const [flagPrat, setFlagPrat] = React.useState<{
    icon: React.ReactNode;
    selected: boolean;
    style: string;
    isSelected?: React.ReactNode;
  }>({
    icon: <IconStar />,
    selected: false,
    style: 'favorite',
  });

  const patientsList = account?.userPatientInformation?.userPatientProfiles;
  const practitionerId = card?.professional?.practitionerId;
  const cpsId = card?.professional?.cpsId;
  const isClientDocavenue = card?.settings?.clientDocavenue;

  const {
    center: { id: centerId },
  } = card;

  const isReferringPrat = patientsList?.find(
    patient =>
      patient?.referringPractitioner?.practitionerId === practitionerId,
  )?.referringPractitioner?.practitionerId;

  const cardPictures = React.useMemo(() => pictures(card), [card]);

  const isFavorite = React.useMemo(
    () =>
      patientsList?.reduce(
        (result, profile) =>
          result ||
          profile.favoritePractitioners?.some(
            favoritePractitioner =>
              favoritePractitioner.centerId === centerId &&
              favoritePractitioner.practitionerId === practitionerId,
          ) ||
          false,
        false,
      ),
    [account, centerId, practitionerId],
  );

  React.useEffect(() => {
    if (!isFavorite && !isReferringPrat) {
      setFlagPrat({
        icon: <IconStar />,
        isSelected: <IconStarFilled aria-label={t('in_favorites')} />,
        selected: false,
        style: 'favorite',
      });
    }

    if (isReferringPrat || (isFavorite && isReferringPrat)) {
      setFlagPrat({
        icon: <IconHeart />,
        isSelected: (
          <IconHeartFilled aria-label={t('my_attending_physician')} />
        ),
        selected: true,
        style: 'referring',
      });
      return;
    }

    if (isFavorite && !isReferringPrat) {
      setFlagPrat({
        icon: <IconStar />,
        isSelected: <IconStarFilled aria-label={t('in_favorites')} />,
        selected: true,
        style: 'favorite',
      });
    }
  }, [isReferringPrat, isFavorite]);

  const availableCards =
    cards?.filter(
      cardData =>
        cardData.center?.isVisibleOnline && !cardData.center?.isSecret,
    ) || [];

  return (
    <div className="profile-header">
      <div className="profile-header__prat">
        <Avatar
          picturesNbr={cardPictures.length}
          onClick={() => {
            if (cardPictures.length > 0) setOpenSlider(true);

            analyticsEvent({
              category: 'PractitionerOrCenterPage',
              action: 'ClickPicture',
              label: cardPictures.length > 0 ? 'hasPicture' : 'defaultPicture',
            });
          }}
          type={card?.type}
          size="big"
          // WTF is this nonsense, need to refactor this asap.
          source={getAvatar({
            cardHcd: card,
            substituteData:
              router.query.fromImmediateTlc && isSubstitute
                ? card.substituteData
                : null,
          })}
          isOnline={isOnlineTlc(card)}
          title={fullName}
          aria-label={fullName}
          data-testid="practitioner-profile-picture"
        />
        <div className="name-n-spe">
          <Typography
            className="name"
            variant="h1"
            sx={{ mb: 0 }}
            data-testid="practitioner-name"
          >
            <span>{fullName}</span>
            {isClientDocavenue && (
              <IconSwitcher
                className={flagPrat.style}
                on={flagPrat.selected}
                onIcon={flagPrat.isSelected}
                offIcon={flagPrat.icon}
                onClick={async () => {
                  if (isEmpty(account)) {
                    return router.push({
                      pathname: APP_ROUTES.LOGIN,
                      query: { next: card.url, fromPreferences: true },
                    });
                  }
                  if (
                    account?.userPatientInformation?.userPatientProfiles
                      ?.length === 1 &&
                    !cpsId
                  ) {
                    await asyncActions(
                      dispatch,
                      favoritePractitionersActions.updateManyPost(
                        [
                          {
                            profileId:
                              account?.userPatientInformation
                                ?.userPatientProfiles?.[0].id,
                            type:
                              isFavorite || isReferringPrat
                                ? 'NONE'
                                : 'FAVORITE',
                          },
                        ],
                        {
                          params: {
                            centerId,
                            practitionerId,
                          },
                        },
                      ),
                    );
                    const user = await asyncActions(
                      dispatch,
                      usersActions.getOne('me'),
                    );
                    dispatch(
                      authActions.setItem({
                        ...account,
                        ...user,
                      }),
                    );
                    let message;
                    if (isFavorite) message = 'prat_removed_from_care_team';
                    else message = 'prat_added_to_care_team';
                    dispatch(
                      enqueueSnackbar({
                        message: t(message),
                        severity: 'success',
                        duration: 1500,
                      }),
                    );
                  } else {
                    setShowFavoriteSelector(true);
                  }
                  analyticsEvent({
                    category: 'PractitionerOrCenterPage',
                    action: 'ClickFavorite',
                  });
                }}
                data-testid="star-icon"
              />
            )}
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mb: 1 }} alignItems="center">
            <Typography
              variant="body1"
              component="h2"
              data-testid="speciality-section"
            >
              {getSpecialityName(card)}
            </Typography>
            {availableCards.length > 1 && (
              <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                {t('locations', { count: availableCards.length })}
              </Typography>
            )}
          </Stack>
        </div>
        <RDVButton card={card} />
      </div>
      <Dialog
        open={showFavoriteSelector}
        onClose={() => setShowFavoriteSelector(false)}
        title={t('favorite_doctor_profile_selector_title', { fullName })}
      >
        <FavoriteSelector
          account={account}
          practitionerId={get(card, 'professional.practitionerId')}
          centerId={get(card, 'center.id')}
          onClose={() => setShowFavoriteSelector(false)}
        />
      </Dialog>
      {cardPictures && (
        <DialogSlider open={openSlider} setOpen={setOpenSlider}>
          {cardPictures.map(picture => (
            <img
              key={picture?.s3Id}
              src={`/files/${picture?.s3Id}`}
              alt="images"
            />
          ))}
        </DialogSlider>
      )}
    </div>
  );
};

export default PratHeader;
